// COP — TDFLite client (idplite login + policy + config discovery).
//
// Talks to the TDFLite single binary through the caddy HTTPS gateway as ONE
// same-origin surface (no CORS, no mixed-content). Port-resilient: we discover
// backend URLs from /api/config rather than hardcoding them. The base is "" by
// default (same origin via the gateway); set VITE_TDFLITE_BASE to point elsewhere.

import type { PolicyBundle, SubjectClaims } from "../../../../packages/abac-pdp/src/index.ts";
import * as cert from "./cert.ts";
import { clearKasToken } from "./kas.ts";
import { exchangeCodeForSession, silentGoogleReauth, type GoogleCallback } from "./google-oidc.ts";

const BASE = (import.meta.env?.VITE_TDFLITE_BASE as string | undefined) ?? "";
// opentdf-public is the OIDC client the KAS accepts for client-side rewrap (matches
// TDFLite's demoweb). The login JWT it mints IS the rewrap subject.
const CLIENT_ID = (import.meta.env?.VITE_TDFLITE_CLIENT_ID as string | undefined) ?? "opentdf-public";

// Service identities (non-human producers, e.g. the TAK gateway) are real bundle
// entities but are NOT login personas — hide them from the COP's persona picker.
const SERVICE_IDENTITIES = new Set(["tak-gateway"]);

/** Discovery doc from /api/config — the resilience anchor (we read URLs from here). */
export interface TdfliteConfig {
  issuer: string;
  discovery_url: string;
  platform: string;
  kas: string;
  namespace: string;
  jwk_thumbprint?: string;
  display_name?: string;
}

/** A dev login persona (from /api/dev-identities) — username + derived password. */
export interface DevIdentity {
  username: string;
  password: string;
  summary: string;
  admin: boolean;
}

/** A logged-in session: the JWT + its decoded claims (the ABAC subject). */
export interface Session {
  username: string;
  token: string;
  /** Decoded JWT payload — its attribute-named claims ARE the PDP subject. */
  claims: SubjectClaims;
  /** A short clearance summary for the banner / "logged in as" affordance. */
  summary: string;
  expiresAt: number;
  /** The OAuth refresh token (idplite issues one on the PASSWORD grant, 7-day TTL, ROTATED on
   *  each use). Held so the client can silently mint a fresh access token before the ~5-min
   *  access token expires — WITHOUT re-prompting for a password (which is never persisted).
   *  FEDERATED (Google) sessions get NONE by design (re-auth goes back through Google) — they
   *  carry `federated` instead and renew via the silent-Google path. Absent + not federated ⇒
   *  the persona re-logs in on expiry. */
  refreshToken?: string;
  /** Google profile display name (federation logins only) — for the avatar/label. */
  name?: string;
  /** Google profile picture URL (federation logins only) — for the avatar. */
  picture?: string;
  /** True for a FEDERATED (Google) session. Routes the boot refresher to the SILENT GOOGLE
   *  re-auth path (google-oidc.silentGoogleReauth) instead of idplite's refresh_token grant —
   *  federated identities hold no idplite refresh token (the Google session is the source of
   *  truth, re-verified on every renew). Set at mint in google-oidc.sessionFromAccessToken. */
  federated?: boolean;
}

const TOKEN_KEY = "tak-tdf.session";

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  return (await r.json()) as T;
}

/** Decode a JWT payload (no verification — the server already signed it; the COP
 *  only reads claims, it does not trust them for security decisions: the KAS does). */
function decodeJwt(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json) as Record<string, unknown>;
}

/** A compact clearance summary from the claims, for the UI. */
function summarize(claims: Record<string, unknown>): string {
  const cls = String(claims.classification ?? "").toUpperCase();
  const arr = (k: string): string[] => (Array.isArray(claims[k]) ? (claims[k] as string[]) : []);
  const sci = arr("sci_compartment").map((s) => s.toUpperCase());
  const rel = arr("releasability").map((s) => s.toUpperCase());
  const cav = arr("handling_caveat").map((s) => s.toUpperCase());
  const parts = [cls];
  if (sci.length) parts.push(sci.join("/"));
  if (cav.length) parts.push(cav.join("/"));
  if (rel.length) parts.push("REL " + rel.join(","));
  return parts.filter(Boolean).join(" · ");
}

export const tdflite = {
  /** /api/config — discover the issuer / KAS / platform / namespace. */
  config(): Promise<TdfliteConfig> {
    return getJson<TdfliteConfig>("/api/config");
  },

  /** /api/dev-identities — the persona picker (usernames + derived passwords).
   *  Service identities (tak-gateway) are filtered out — they're producers, not logins. */
  async devIdentities(): Promise<DevIdentity[]> {
    const all = await getJson<DevIdentity[]>("/api/dev-identities");
    return all.filter((d) => !SERVICE_IDENTITIES.has(d.username));
  },

  /** /api/policy-attrs — the LIVE policy bundle for the PDP. Fails CLOSED: if the
   *  live bundle is unreachable we do NOT substitute a stale/wrong-namespace seed
   *  (that would mis-decide visibility) — the caller keeps the loading state. */
  policyBundle(): Promise<PolicyBundle> {
    return getJson<PolicyBundle>("/api/policy-attrs");
  },

  /** idplite ROPC password grant → a real JWT session. */
  async login(username: string, password: string): Promise<Session> {
    const body = new URLSearchParams({
      grant_type: "password",
      username,
      password,
      client_id: CLIENT_ID,
      scope: "openid profile",
    });
    const r = await fetch(`${BASE}/idp/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    const j = (await r.json()) as Record<string, unknown>;
    if (!r.ok || typeof j.access_token !== "string") {
      throw new Error(String(j.error_description ?? j.error ?? `login failed (${r.status})`));
    }
    const token = j.access_token;
    const claims = decodeJwt(token);
    const session: Session = {
      username,
      token,
      claims: claims as SubjectClaims,
      summary: summarize(claims),
      expiresAt: typeof claims.exp === "number" ? claims.exp * 1000 : Date.now() + 5 * 60_000,
      refreshToken: typeof j.refresh_token === "string" ? j.refresh_token : undefined,
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));

    // FAIL-SOFT CERT LOAD (Phase 3). After the real login succeeds, ALSO try to fetch +
    // decrypt this user's PKI cert envelope and hold the signing key in memory (host/cert.ts).
    // This is purely ADDITIVE: any failure (vault down, 404, wrong password, tampered/cross-user
    // blob → GCM throws) is logged and SWALLOWED — login still returns success and the COP enters
    // in a "no cert" state. Login must NEVER block on the cert.
    // Drop any PRIOR persona's cert FIRST (persona switch re-calls login()): the new cert is
    // loaded only on a fresh successful fetch+decrypt below, so a 404/down vault / decrypt
    // failure can never leave the previous operator's signing key + PEMs in memory. On the
    // first login state is null (no-op). Mirrors the copilot/chat/kas clear-on-switch discipline.
    cert.clearCert();
    // Bound the fail-soft fetch with a short timeout: a black-holed/stalled cert-vault (TCP
    // drop, not a fast 404/502) must NOT hang the awaited fetch and stall the login spinner.
    // An abort is treated like any other fail-soft cert failure (warn + enter without a cert).
    const certAbort = new AbortController();
    const certTimer = setTimeout(() => certAbort.abort(), 4000);
    try {
      const cr = await fetch(`${BASE}/api/cert-bundle/${encodeURIComponent(username)}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        signal: certAbort.signal,
      });
      if (cr.ok) {
        await cert.loadCertFromEnvelope(await cr.json(), password, username);
      } else {
        console.warn(`[cert] cert-bundle ${username} → ${cr.status} (entering COP without a cert)`);
      }
    } catch (e) {
      console.warn("[cert] cert load failed (fail-soft — entering COP without a cert):", e);
    } finally {
      clearTimeout(certTimer);
    }

    return session;
  },

  /** "Sign in with Google" → a real TDFLite JWT session. The browser ships the
   *  Google authorization CODE (+ PKCE verifier + redirect_uri + nonce) to the
   *  TRUSTED IdP's /idp/federate/google endpoint (BFF); the IdP redeems the code
   *  SERVER-SIDE with the confidential client_secret a SPA must not hold, verifies
   *  the resulting id_token (sig + iss + aud + exp + email_verified), and mints a
   *  TDFLite JWT whose clearance is derived from the verified email DOMAIN — never
   *  by this client. The returned token has the SAME shape as the password path,
   *  so the session/KAS path is unchanged.
   *
   *  Federated users authenticate via Google, not a bundle password, so there is
   *  NO password to decrypt a PKI cert envelope — we skip the cert load (entering
   *  the COP without a cert) but still drop any prior persona's cert so no identity
   *  material survives the switch (mirrors login()'s fail-closed cert discipline). */
  async federateGoogle(cb: GoogleCallback): Promise<Session> {
    const session = await exchangeCodeForSession(cb);
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
    cert.clearCert();
    return session;
  },

  /** Restore a non-expired session from sessionStorage, if any. */
  restore(): Session | null {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    try {
      const s = JSON.parse(raw) as Session;
      if (s.expiresAt > Date.now() + 5_000) return s;
    } catch {
      /* fall through */
    }
    sessionStorage.removeItem(TOKEN_KEY);
    return null;
  },

  /** Read the persisted session WITHOUT the expiry gate restore() applies. Used by the
   *  silent-refresh path: the ACCESS token may be expired (that's the whole point) but the
   *  REFRESH token is still valid, so peek() must not drop the session the way restore() does. */
  peek(): Session | null {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Session;
    } catch {
      return null;
    }
  },

  /** Silently mint a FRESH access token from the session's refresh token — no password,
   *  no re-prompt (idplite `grant_type=refresh_token`, verified live on prod: rotates the
   *  refresh token + preserves the full clearance claims). Persists the ROTATED session to
   *  sessionStorage so a page reload restores a usable refresh token (the crux the stock
   *  SDK provider gets wrong: it rotates in memory only). Returns the new Session, or null
   *  on any failure (no refresh token, network error, superseded/expired refresh) — the
   *  caller then falls closed exactly as before (a genuine re-login is required). */
  async refresh(session: Session): Promise<Session | null> {
    if (!session.refreshToken) return null;
    try {
      const r = await fetch(`${BASE}/idp/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: session.refreshToken,
          client_id: CLIENT_ID,
        }),
      });
      const j = (await r.json()) as Record<string, unknown>;
      if (!r.ok || typeof j.access_token !== "string") return null;
      const token = j.access_token;
      const claims = decodeJwt(token);
      const next: Session = {
        ...session, // preserve username / name / picture
        token,
        claims: claims as SubjectClaims,
        summary: summarize(claims),
        expiresAt: typeof claims.exp === "number" ? claims.exp * 1000 : Date.now() + 5 * 60_000,
        // idplite ROTATES on each use — carry the new refresh token forward (fall back to the
        // old only if the server omitted one, which it does not for this grant).
        refreshToken: typeof j.refresh_token === "string" ? j.refresh_token : session.refreshToken,
      };
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(next));
      return next;
    } catch {
      return null;
    }
  },

  /** FEDERATED (Google) silent renewal — the symmetric counterpart to refresh() for sessions that
   *  hold NO idplite refresh token (federation issues none by design: re-auth goes back through
   *  Google). Re-verifies the LIVE Google session (prompt=none) and re-exchanges via the SAME
   *  stateless BFF (google-oidc.silentGoogleReauth), minting a fresh access token, then PERSISTS
   *  the rotated session so a reload / future renewal sees it. Returns the fresh Session, or null
   *  on any failure (Google session gone, FedCM/frame blocked, exchange rejected) — the caller
   *  then falls closed to the honest SESSION EXPIRED banner.
   *
   *  SECURITY POSTURE: NO idplite refresh token is minted or stored for a federated identity —
   *  the persisted session carries none (silentGoogleReauth returns none). The Google session
   *  remains the source of truth, RE-VERIFIED on every renew. Token acquisition only; never
   *  touches the PDP/KAS release decision. */
  async refreshFederated(session: Session): Promise<Session | null> {
    const next = await silentGoogleReauth({ loginHint: session.username });
    if (!next) return null;
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(next));
    return next;
  },

  logout(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    // Drop the in-memory PKI cert AND zeroize the cached KAS rewrap token so no
    // identity material survives a sign-out (fail-closed logout).
    cert.clearCert();
    clearKasToken();
  },
};
